import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import SortByAlphaIcon from "@mui/icons-material/SortByAlpha";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useDispatch, useSelector } from "react-redux";
import { findByTurma, ordenar, remove } from "./api-aluno-turma";
import { removeAlunosTurma, setAlunosTurma, setAlunoTurma } from "./actions/actions";
import { APROVADO, resultadoFinalText } from "./ResultadoFinal";
import { insertByAlunoTurma, deleteByAlunoTurma } from "../NotaBimestre/api-nota-bimestre";
import { insertByStudent as insertPresencaAlunoByAlunoTurma } from "../PresencaAluno/api-presenca-aluno";
import { insertConceitoAlunoByAlunoTurma } from "../ConceitoAluno/api-conceito-aluno";
import { insertAlunoFrequenciaByAlunoTurma } from "../AlunoFrequencia/api-aluno-frequencia";
import { ADMIN, COORDENADOR } from "../Usuario/Perfil";
import { dateFormat } from "../../util/DateUtil";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { success } from "../../util/ToastUtil";
import Loading from "../UI/Loading";

// Ações por aluno (somente gestores). Todas são irreversíveis.
const ACOES_ALUNO = [
  {
    label: "Adicionar Notas Bimestre",
    message: "Deseja adicionar todas as Notas Bimestre para este aluno?",
    request: insertByAlunoTurma,
    successText: "Notas bimestre adicionadas.",
  },
  {
    label: "Remover Notas Bimestre",
    message: "Deseja remover todas as Notas Bimestre deste aluno?",
    request: deleteByAlunoTurma,
    successText: "Todas as notas bimestre do aluno foram removidas.",
    danger: true,
  },
  {
    label: "Adicionar Presenças",
    message: "Deseja adicionar todas as Presenças para este aluno?",
    request: insertPresencaAlunoByAlunoTurma,
    successText: "Presenças adicionadas.",
  },
  {
    label: "Adicionar Conceitos",
    message: "Deseja adicionar todos os Conceitos para este aluno?",
    request: insertConceitoAlunoByAlunoTurma,
    successText: "Conceitos adicionados.",
  },
  {
    label: "Adicionar Aluno Frequência",
    message: "Deseja adicionar os registros de Aluno Frequência para este aluno?",
    request: insertAlunoFrequenciaByAlunoTurma,
    successText: "Aluno Frequência adicionado.",
  },
];

// Referência estável para o useMemo quando o Redux ainda não tem a lista.
const SEM_ALUNOS = [];

const normalize = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const confirm = (message, onConfirm) =>
  confirmAlert({
    title: "Alerta",
    message,
    buttons: [
      { label: "Sim", onClick: onConfirm },
      { label: "Não", onClick: () => {} },
    ],
  });

export default function AlunoTurmaTable() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.usuario);
  const turma = useSelector((state) => state.turma);
  const alunosTurma = useSelector((state) => state.alunosTurma) ?? SEM_ALUNOS;
  const isGestor = user?.perfil === ADMIN || user?.perfil === COORDENADOR;
  const [isLoading, setIsLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [menu, setMenu] = useState({ anchorEl: null, alunoTurma: null });
  const codTurma = turma?.codigo;

  useEffect(() => {
    if (!codTurma) return undefined;
    let active = true;
    setIsLoading(true);
    findByTurma(codTurma)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else if (Array.isArray(data)) {
          dispatch(setAlunosTurma(data));
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [codTurma, dispatch]);

  const visiveis = useMemo(() => {
    const termo = normalize(busca.trim());
    return [...alunosTurma]
      .filter((at) => !termo || normalize(at.aluno?.nome).includes(termo))
      .sort((a, b) => (a.numeroChamada ?? Infinity) - (b.numeroChamada ?? Infinity));
  }, [alunosTurma, busca]);

  const totalAtivos = alunosTurma.filter((at) => at.ativo).length;

  const handleDelete = (at) => {
    confirm(
      `ATENÇÃO: ao remover ${at.aluno?.nome ?? "o aluno"} da turma, todas as presenças, atividades e notas de todos os bimestres serão perdidas. Deseja continuar?`,
      () => {
        remove(at.codigo).then((data) => {
          if (!data || data.error) {
            errorMessage(data?.message || "Não foi possível remover o aluno da turma.");
            return;
          }
          dispatch(removeAlunosTurma(at.codigo));
          success("Aluno removido da turma.");
        });
      },
    );
  };

  const handleOrdenar = () => {
    confirm("Deseja renumerar a chamada de todos os alunos em ordem alfabética?", () => {
      ordenar(codTurma).then((data) => {
        if (!data || data.error) {
          errorMessage(data?.message || "Não foi possível ordenar os alunos.");
          return;
        }
        dispatch(setAlunosTurma(data));
        success("Número de chamada reordenado.");
      });
    });
  };

  const handleAcaoAluno = (acao) => {
    const at = menu.alunoTurma;
    setMenu({ anchorEl: null, alunoTurma: null });
    confirm(`${acao.message} Após confirmada, esta ação não poderá ser desfeita.`, () => {
      acao.request(at.codigo).then((data) => {
        if (!data || data.error) {
          errorMessage(data?.message || "Não foi possível concluir a operação.");
          return;
        }
        success(acao.successText);
      });
    });
  };

  const renderTabela = () => {
    if (isLoading) {
      return (
        <Box py={6}>
          <Loading description="Carregando alunos da turma..." />
        </Box>
      );
    }
    if (alunosTurma.length === 0) {
      return (
        <Box
          sx={{
            py: 6,
            textAlign: "center",
            color: "text.secondary",
            border: 1,
            borderStyle: "dashed",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <GroupsOutlinedIcon fontSize="large" />
          <Typography variant="body2" mt={1}>
            Nenhum aluno nesta turma.
          </Typography>
        </Box>
      );
    }
    if (visiveis.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
          Nenhum aluno corresponde à busca.
        </Typography>
      );
    }
    return (
      <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
        <Table size="small" sx={{ minWidth: 760 }} aria-label="Alunos da turma">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover" } }}>
              <TableCell sx={{ width: 56 }} align="center">
                Nº
              </TableCell>
              <TableCell>Aluno</TableCell>
              <TableCell>Entrada</TableCell>
              <TableCell>Saída</TableCell>
              <TableCell>Resultado Final</TableCell>
              <TableCell>Situação</TableCell>
              <TableCell align="right" sx={{ width: 130 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visiveis.map((at) => (
              <TableRow key={at.codigo} hover sx={{ opacity: at.ativo ? 1 : 0.65 }}>
                <TableCell align="center">{at.numeroChamada ?? "--"}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {at.aluno?.nome}
                  </Typography>
                  {at.aluno?.codAlunoEscola && (
                    <Typography variant="caption" color="text.secondary">
                      Código {at.aluno.codAlunoEscola}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{at.dataEntrada ? dateFormat(at.dataEntrada) : "--"}</TableCell>
                <TableCell>{at.dataSaida ? dateFormat(at.dataSaida) : "--"}</TableCell>
                <TableCell>
                  {at.resultadoFinalEnum ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={at.resultadoFinalEnum === APROVADO ? "success" : "error"}
                      label={resultadoFinalText(at.resultadoFinalEnum)}
                    />
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      --
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={at.ativo ? "success" : "default"}
                    label={at.ativo ? "Ativo" : "Desativado"}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => dispatch(setAlunoTurma(at))}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remover da turma">
                    <IconButton size="small" onClick={() => handleDelete(at)}>
                      <DeleteOutlineIcon fontSize="small" color="error" />
                    </IconButton>
                  </Tooltip>
                  {isGestor && (
                    <Tooltip title="Mais ações">
                      <IconButton
                        size="small"
                        onClick={(event) => setMenu({ anchorEl: event.currentTarget, alunoTurma: at })}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} mb={2} alignItems={{ md: "center" }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => dispatch(setAlunoTurma({ turma, eNovo: true }))}
          sx={{ flexShrink: 0 }}
        >
          Adicionar Aluno
        </Button>
        {alunosTurma.length > 0 && (
          <Button variant="outlined" startIcon={<SortByAlphaIcon />} onClick={handleOrdenar} sx={{ flexShrink: 0 }}>
            Ordenar Nº Chamada
          </Button>
        )}
        <Box flexGrow={1} />
        <TextField
          size="small"
          placeholder="Buscar aluno"
          value={busca}
          onChange={(event) => setBusca(event.target.value)}
          sx={{ minWidth: { md: 260 } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Stack>

      {!isLoading && alunosTurma.length > 0 && (
        <Typography variant="body2" color="text.secondary" mb={1}>
          {alunosTurma.length} aluno(s) · {totalAtivos} ativo(s)
        </Typography>
      )}

      {renderTabela()}

      <Menu
        anchorEl={menu.anchorEl}
        open={Boolean(menu.anchorEl)}
        onClose={() => setMenu({ anchorEl: null, alunoTurma: null })}
      >
        {ACOES_ALUNO.map((acao) => (
          <MenuItem key={acao.label} onClick={() => handleAcaoAluno(acao)} sx={acao.danger ? { color: "error.main" } : undefined}>
            <ListItemIcon>
              {acao.danger ? (
                <DeleteForeverOutlinedIcon fontSize="small" color="error" />
              ) : (
                <AddBoxOutlinedIcon fontSize="small" color="success" />
              )}
            </ListItemIcon>
            {acao.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
